import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Undo, Redo, Save, ArrowLeft, Plus, FileText, Trash2,
  Edit3, Copy, Loader2, Tag, ChevronDown, Settings, X
} from 'lucide-react';
import { toast } from 'sonner';
import './DocumentTemplates.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
  'Content-Type': 'application/json'
});

// ── Tag Inserter Dropdown ──
const TagInserter = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/template-tags`, { headers: getHeaders() })
      .then(r => r.json())
      .then(setTags)
      .catch(() => {});
  }, []);

  return (
    <div className="doc-tmpl-tag-inserter" data-testid="tag-inserter">
      <button
        className="doc-tmpl-toolbar-btn doc-tmpl-tag-btn"
        onClick={() => setOpen(!open)}
        title="Insert template tag"
        type="button"
      >
        <Tag size={16} />
        <span>Insert Tag</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="doc-tmpl-tag-dropdown">
          {tags.filter(t => t.source !== 'signature').map(t => (
            <button
              key={t.tag}
              className="doc-tmpl-tag-option"
              onClick={() => { onInsert(t.tag); setOpen(false); }}
              type="button"
              data-testid={`tag-${t.tag}`}
            >
              <code>{t.tag}</code>
              <span>{t.description}</span>
            </button>
          ))}
          <div className="doc-tmpl-tag-divider">Signature Blocks</div>
          {tags.filter(t => t.source === 'signature').map(t => (
            <button
              key={t.tag}
              className="doc-tmpl-tag-option doc-tmpl-tag-sig"
              onClick={() => { onInsert(t.tag); setOpen(false); }}
              type="button"
              data-testid={`tag-${t.tag}`}
            >
              <code>{t.tag}</code>
              <span>{t.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Custom Fields Editor ──
const CustomFieldsEditor = ({ fields, onChange }) => {
  const [expanded, setExpanded] = useState(false);

  const addField = () => {
    onChange([...fields, { name: '', label: '', required: false }]);
    setExpanded(true);
  };

  const removeField = (idx) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const updateField = (idx, key, value) => {
    const updated = fields.map((f, i) => i === idx ? { ...f, [key]: value } : f);
    onChange(updated);
  };

  // Auto-generate tag name from label — single update to avoid stale closure
  const handleLabelChange = (idx, label) => {
    const name = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const updated = fields.map((f, i) => i === idx ? { ...f, label, name } : f);
    onChange(updated);
  };

  return (
    <div className="doc-tmpl-custom-fields-editor" data-testid="custom-fields-editor">
      <div className="doc-tmpl-cf-header" onClick={() => setExpanded(!expanded)}>
        <Settings size={16} />
        <span>Custom Fields ({fields.length})</span>
        <ChevronDown size={14} className={expanded ? 'rotated' : ''} />
      </div>
      {expanded && (
        <div className="doc-tmpl-cf-body">
          <p className="doc-tmpl-cf-hint">
            Define fields that staff must fill in when generating this document. 
            Use the tag <code>{'{{field_name}}'}</code> in the template body.
          </p>
          {fields.map((f, idx) => (
            <div key={idx} className="doc-tmpl-cf-row" data-testid={`custom-field-${idx}`}>
              <input
                type="text"
                placeholder="Label (e.g. Release Title)"
                value={f.label}
                onChange={e => handleLabelChange(idx, e.target.value)}
              />
              <code className="doc-tmpl-cf-tag">{`{{${f.name || '...'}}}`}</code>
              <label className="doc-tmpl-cf-req">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={e => updateField(idx, 'required', e.target.checked)}
                />
                Required
              </label>
              <button className="doc-tmpl-cf-remove" onClick={() => removeField(idx)} type="button">
                <X size={14} />
              </button>
            </div>
          ))}
          <button className="doc-tmpl-cf-add" onClick={addField} type="button" data-testid="add-custom-field">
            <Plus size={14} /> Add Field
          </button>
        </div>
      )}
    </div>
  );
};

// ── Rich Text Toolbar ──
const EditorToolbar = ({ editor, onInsertTag }) => {
  if (!editor) return null;

  return (
    <div className="doc-tmpl-editor-toolbar" data-testid="editor-toolbar">
      <div className="doc-tmpl-toolbar-group">
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`} type="button"><Bold size={16} /></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`} type="button"><Italic size={16} /></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`} type="button"><UnderlineIcon size={16} /></button>
      </div>
      <div className="doc-tmpl-toolbar-group">
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`} type="button">H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`} type="button">H2</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`} type="button">H3</button>
      </div>
      <div className="doc-tmpl-toolbar-group">
        <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`doc-tmpl-toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`} type="button"><AlignLeft size={16} /></button>
        <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`doc-tmpl-toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`} type="button"><AlignCenter size={16} /></button>
        <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`doc-tmpl-toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`} type="button"><AlignRight size={16} /></button>
      </div>
      <div className="doc-tmpl-toolbar-group">
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`} type="button"><List size={16} /></button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`doc-tmpl-toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`} type="button"><ListOrdered size={16} /></button>
      </div>
      <div className="doc-tmpl-toolbar-group">
        <button onClick={() => editor.chain().focus().undo().run()} className="doc-tmpl-toolbar-btn" type="button"><Undo size={16} /></button>
        <button onClick={() => editor.chain().focus().redo().run()} className="doc-tmpl-toolbar-btn" type="button"><Redo size={16} /></button>
      </div>
      <TagInserter onInsert={onInsertTag} />
    </div>
  );
};

// ── Template Editor View ──
const TemplateEditorView = ({ template, onSave, onBack }) => {
  const [title, setTitle] = useState(template?.title || '');
  const [docType, setDocType] = useState(template?.document_type || 'contract');
  const [description, setDescription] = useState(template?.description || '');
  const [customFields, setCustomFields] = useState(template?.custom_fields || []);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Start writing your document template...' }),
    ],
    content: template?.content_html || '',
  });

  const handleInsertTag = useCallback((tag) => {
    if (editor) {
      editor.chain().focus().insertContent(tag).run();
    }
  }, [editor]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    if (!editor?.getHTML() || editor.isEmpty) { toast.error('Document content is empty'); return; }

    // Validate custom fields have names
    const invalidFields = customFields.filter(f => f.label && !f.name);
    if (invalidFields.length > 0) { toast.error('All custom fields need a label'); return; }

    setSaving(true);
    try {
      const body = {
        title,
        content_html: editor.getHTML(),
        document_type: docType,
        description: description || null,
        custom_fields: customFields.filter(f => f.name && f.label),
      };

      const url = template?.id
        ? `${API_URL}/api/staff/document-templates/${template.id}`
        : `${API_URL}/api/staff/document-templates`;

      const res = await fetch(url, {
        method: template?.id ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(template?.id ? 'Template updated' : 'Template created');
        onSave();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="doc-tmpl-editor" data-testid="template-editor">
      <div className="doc-tmpl-editor-header">
        <button className="doc-tmpl-back-btn" onClick={onBack} type="button" data-testid="editor-back">
          <ArrowLeft size={18} />
          Back
        </button>
        <h2>{template?.id ? 'Edit Template' : 'New Template'}</h2>
        <button className="doc-tmpl-save-btn" onClick={handleSave} disabled={saving} data-testid="save-template">
          {saving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>

      <div className="doc-tmpl-editor-meta">
        <div className="doc-tmpl-meta-field">
          <label>Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Artist Distribution Agreement"
            data-testid="template-title"
          />
        </div>
        <div className="doc-tmpl-meta-field">
          <label>Type</label>
          <select value={docType} onChange={e => setDocType(e.target.value)} data-testid="template-type">
            <option value="contract">Contract</option>
            <option value="agreement">Agreement</option>
            <option value="nda">NDA</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="doc-tmpl-meta-field doc-tmpl-desc">
          <label>Description (optional)</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Brief description of this template"
          />
        </div>
      </div>

      <CustomFieldsEditor fields={customFields} onChange={setCustomFields} />

      <div className="doc-tmpl-editor-sticky-wrapper">
        <EditorToolbar editor={editor} onInsertTag={handleInsertTag} />
        <div className="doc-tmpl-letter-paper-scroll">
          <div className="doc-tmpl-letter-paper" data-testid="letter-paper">
            <EditorContent editor={editor} className="doc-tmpl-editor-content" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Template List View ──
const DocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/document-templates`, { headers: getHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/document-templates/${id}`, {
        method: 'DELETE', headers: getHeaders()
      });
      if (res.ok) { toast.success('Template deleted'); fetchTemplates(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (template) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/document-templates`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          title: `${template.title} (Copy)`,
          content_html: template.content_html,
          document_type: template.document_type,
          description: template.description,
          custom_fields: template.custom_fields || [],
        }),
      });
      if (res.ok) { toast.success('Template duplicated'); fetchTemplates(); }
    } catch { toast.error('Failed to duplicate'); }
  };

  // Show editor view
  if (editingTemplate || creating) {
    return (
      <TemplateEditorView
        template={editingTemplate}
        onSave={() => { setEditingTemplate(null); setCreating(false); fetchTemplates(); }}
        onBack={() => { setEditingTemplate(null); setCreating(false); }}
      />
    );
  }

  return (
    <div className="doc-tmpl-page" data-testid="document-templates">
      <div className="doc-tmpl-header">
        <div>
          <h2>Document Templates</h2>
          <p>Create reusable templates with auto-fill tags for artist data</p>
        </div>
        <button className="doc-tmpl-create-btn" onClick={() => setCreating(true)} data-testid="create-template">
          <Plus size={16} />
          New Template
        </button>
      </div>

      {loading ? (
        <div className="doc-tmpl-loading"><Loader2 size={24} className="spinner" /> Loading...</div>
      ) : templates.length === 0 ? (
        <div className="doc-tmpl-empty" data-testid="no-templates">
          <FileText size={48} />
          <h3>No Templates Yet</h3>
          <p>Create your first document template to start generating contracts and agreements.</p>
          <button className="doc-tmpl-create-btn" onClick={() => setCreating(true)}>
            <Plus size={16} /> Create Template
          </button>
        </div>
      ) : (
        <div className="doc-tmpl-grid">
          {templates.map(t => (
            <div key={t.id} className="doc-tmpl-card" data-testid={`template-${t.id}`}>
              <div className="doc-tmpl-card-header">
                <FileText size={20} />
                <span className="doc-tmpl-type-badge">{t.document_type}</span>
              </div>
              <h3>{t.title}</h3>
              {t.description && <p className="doc-tmpl-card-desc">{t.description}</p>}
              {t.custom_fields?.length > 0 && (
                <div className="doc-tmpl-card-fields">
                  <Settings size={12} />
                  <span>{t.custom_fields.length} custom field{t.custom_fields.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="doc-tmpl-card-meta">
                <span>Updated {new Date(t.updated_at).toLocaleDateString()}</span>
              </div>
              <div className="doc-tmpl-card-actions">
                <button onClick={() => setEditingTemplate(t)} title="Edit" data-testid={`edit-${t.id}`}>
                  <Edit3 size={14} /> Edit
                </button>
                <button onClick={() => handleDuplicate(t)} title="Duplicate">
                  <Copy size={14} /> Duplicate
                </button>
                <button onClick={() => handleDelete(t.id)} title="Delete" className="doc-tmpl-delete">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentTemplates;
